import { Mic } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"

import DrOkaforAvatar from "@/assets/images/dr-okafor/idle.png"
import { Avatar } from "@/components/base/avatar/avatar"
import DebriefSurface from "@/components/chat/DebriefSurface"
import {
  type Message,
  MessageItem,
} from "@/components/untitled-ui/application/messaging/messaging"
import { SimulationMessageAction } from "@/components/untitled-ui/derived/SimulationMessageAction"
import type {
  ConversationEvent,
  SessionDebriefPayload,
} from "@/types/conversation"
import { postJson } from "@/utils/api"

interface SimulationSurfaceProps {
  practitionerName?: string
  practitionerTitle?: string
  practitionerAvatar?: string
  conversationId?: number
  events?: ConversationEvent[]
  // Watermark separating historical backlog from live events. `null` → no live
  // events: render everything statically (still hydrating, or pure history like
  // replay); a cursor string → only beats whose source event cursor is greater
  // animate; `undefined` → no boundary tracked, reveal every beat with ceremony
  // (standalone/legacy).
  liveCursor?: string | null
  onFocus?: () => void
  isFocused?: boolean
  isBlurred?: boolean
  emitEvent?: (
    type: string,
    payload: Record<string, unknown>,
    correlates?: Record<string, unknown>,
  ) => Promise<void>
  userMessageIds?: Set<string>
  readOnly?: boolean
  // Karaoke mode: show a subtle mic beside the "Connected" badge. Its presence
  // means the conversation is still script-driven; it disappears once a fork to
  // live succeeds — the presenter's fork success/failure signal.
  karaokeMode?: boolean
  // The "What we saw" debrief payload (from a session_debrief event). When
  // present, render the debrief card at the foot of the transcript — the finale
  // the funder sees in replay, now on the live /chat surface too.
  debrief?: SessionDebriefPayload
}

function formatTime(ts: number | Date) {
  const d = typeof ts === "number" ? new Date(ts) : ts
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

// Avatar initials from a display name ("Dr Okafor" → "DO"), matching the
// logic MessageItem uses for speech-bubble avatars.
function initialsFor(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
}

// The NPC's display identity is plumbed from the Score persona on the
// npc_response_start / npc_response_complete events, so the UI label always
// matches the character the LLM voices. Scan the latest carrier so a value,
// once set, survives later events that omit it.
function deriveNpcIdentity(events: ConversationEvent[] | undefined): {
  name?: string
  title?: string
} {
  if (!events?.length) return {}
  let name: string | undefined
  let title: string | undefined
  for (const e of events) {
    if (e.type !== "npc_response_start" && e.type !== "npc_response_complete")
      continue
    const payload = e.payload as Record<string, unknown>
    if (typeof payload.npc_name === "string") name = payload.npc_name
    if (typeof payload.npc_title === "string") title = payload.npc_title
  }
  return { name, title }
}

interface RenderItem {
  msg: Message
  sort: number
  // Whether this beat belongs to the live session (vs historical backlog).
  // Only live beats earn the entrance ceremony; historical beats render static.
  live: boolean
}

// Modern chat apps coalesce consecutive messages from one speaker into a run:
// avatar + name + time show once at the top, and a fresh timestamp only returns
// after a real lull. Two beats group when they share a speaker and land within
// this window; a wider gap starts a new group so its time/avatar reappear.
const GROUP_BREAK_MS = 5 * 60 * 1000

// Identity used to decide whether two consecutive beats belong to the same run.
// Narrator asides are keyed uniquely so they always stand alone.
function speakerKey(m: Message): string {
  if (m.variant === "narrator") return `narrator:${m.id}`
  if (m.user.me) return "me"
  return `npc:${m.user.name ?? ""}`
}

function projectEventsToMessages(
  events: ConversationEvent[] | undefined,
  practitionerName: string,
  practitionerAvatar: string | undefined,
  liveCursor: string | null | undefined,
  includeUserMessages = true,
): { items: RenderItem[]; typing: boolean; processedClientIds: Set<string> } {
  const items: RenderItem[] = []
  let typing = false
  let npcBuffer: { text: string; startedAt?: number } | null = null
  const processedClientIds = new Set<string>()

  // An event is live iff its cursor is past the hydration watermark. `undefined`
  // (no boundary tracked) reveals everything; `null` (hydrating) suppresses it.
  const isLive = (e: ConversationEvent) =>
    liveCursor === undefined
      ? true
      : liveCursor === null
        ? false
        : String(e.cursor ?? "") > liveCursor

  if (!events?.length) return { items: [], typing, processedClientIds }

  for (const e of events) {
    const live = isLive(e)
    switch (e.type) {
      case "user_message": {
        if (includeUserMessages) {
          const clientId = e.correlates?.client_msg_id
          if (clientId) processedClientIds.add(clientId)

          const payload = e.payload as Record<string, unknown>
          items.push({
            msg: {
              id: clientId ?? e.event_id,
              sentAt: formatTime(e.server_ts),
              text: String((payload.text as string) ?? ""),
              user: { me: true, name: "You", status: "online" },
            },
            sort:
              typeof e.server_ts === "number"
                ? e.server_ts
                : parseInt(String(e.cursor ?? "0"), 10) || Date.now(),
            live,
          })
        }
        break
      }
      case "npc_response_start": {
        typing = true
        npcBuffer = { text: "", startedAt: e.server_ts }
        break
      }
      case "npc_response_chunk": {
        typing = true
        const chunkPayload = e.payload as Record<string, unknown>
        if (npcBuffer)
          npcBuffer.text += String((chunkPayload.chunk as string) ?? "")
        break
      }
      case "npc_response_complete": {
        const completePayload = e.payload as Record<string, unknown>
        const baseSort =
          typeof e.server_ts === "number"
            ? e.server_ts
            : parseInt(String(e.cursor ?? "0"), 10) || Date.now()
        const segments = completePayload.segments as
          | { kind: string; speaker: string; text: string }[]
          | undefined

        if (Array.isArray(segments) && segments.length > 0) {
          // Structured performance: each action → a narrator aside, each
          // speech → its own bubble. Sub-order by index so segments keep
          // their authored sequence within the turn.
          segments.forEach((seg, idx) => {
            const isAction = seg.kind === "action"
            items.push({
              msg: {
                id: `${e.event_id}:${idx}`,
                sentAt: formatTime(e.server_ts),
                text: seg.text,
                variant: isAction ? "narrator" : "bubble",
                user: isAction
                  ? { name: "Narrator" }
                  : {
                      name: practitionerName,
                      status: "online",
                      avatarUrl: practitionerAvatar,
                    },
              },
              sort: baseSort + idx / 1000,
              live,
            })
          })
        } else {
          // Legacy / developmental / fallback beats: a single bubble from text.
          const text = String(
            (completePayload.text as string) ?? npcBuffer?.text ?? "",
          )
          items.push({
            msg: {
              id: e.event_id,
              sentAt: formatTime(e.server_ts),
              text,
              user: {
                name: practitionerName,
                status: "online",
                avatarUrl: practitionerAvatar,
              },
            },
            sort: baseSort,
            live,
          })
        }
        typing = false
        npcBuffer = null
        break
      }
      // Intentionally ignore stage_transition for chat message rendering.
      // Stage changes should be reflected in dedicated UI (e.g., indicators), not as NPC messages.
      case "stage_transition":
        break
      default:
        // no-op unknown types
        break
    }
  }

  return { items, typing, processedClientIds }
}

export default function SimulationSurface({
  practitionerName = "Dr Okafor",
  practitionerTitle = "GP",
  practitionerAvatar = DrOkaforAvatar,
  conversationId,
  events,
  liveCursor,
  onFocus,
  isFocused = true,
  isBlurred = false,
  readOnly = false,
  emitEvent,
  karaokeMode = false,
  debrief,
}: SimulationSurfaceProps) {
  const [debugOn, setDebugOn] = useState<boolean>(() => {
    if (typeof window === "undefined") return false
    const v = localStorage.getItem("npc_debug")
    return v === "1" || v === "true"
  })

  // Composition tracking state
  const [compositionStartTime, setCompositionStartTime] = useState<
    number | null
  >(null)
  const [draftText, setDraftText] = useState("")
  const [lastMessageTime, setLastMessageTime] = useState<number>(Date.now())
  const compositionTimeoutRef = useRef<number | null>(null)

  // Teleprompter: KaraokePresenter dispatches `karaoke-type` with the next
  // scripted line; we hand it to the input, which types it out and submits.
  const [injection, setInjection] = useState<
    { text: string; nonce: number } | undefined
  >()
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { text?: string } | undefined
      if (detail?.text) setInjection({ text: detail.text, nonce: Date.now() })
    }
    window.addEventListener("karaoke-type", handler as EventListener)
    return () =>
      window.removeEventListener("karaoke-type", handler as EventListener)
  }, [])

  // Auto-scroll: the messages container follows the conversation, staying
  // pinned to the newest beat as it arrives. If the user scrolls up to read
  // history we stop following so new messages don't yank them back down.
  const scrollRef = useRef<HTMLDivElement>(null)
  const stickToBottomRef = useRef(true)

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    stickToBottomRef.current = distanceFromBottom < 80
  }

  useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent)?.type === "npc-debug-changed") {
        const detail = (e as CustomEvent).detail as
          | { value: boolean }
          | undefined
        if (detail && typeof detail.value === "boolean")
          setDebugOn(detail.value)
        else
          setDebugOn(() => {
            const v = localStorage.getItem("npc_debug")
            return v === "1" || v === "true"
          })
      }
    }
    const onStorage = () => {
      const v = localStorage.getItem("npc_debug")
      setDebugOn(v === "1" || v === "true")
    }
    window.addEventListener("npc-debug-changed", handler as EventListener)
    window.addEventListener("storage", onStorage)
    return () => {
      window.removeEventListener("npc-debug-changed", handler as EventListener)
      window.removeEventListener("storage", onStorage)
    }
  }, [])

  // Prefer the persona identity carried on the events; fall back to the props
  // (used only in the brief window before the opening event arrives).
  const npcIdentity = useMemo(() => deriveNpcIdentity(events), [events])
  const resolvedName = npcIdentity.name ?? practitionerName
  const resolvedTitle = npcIdentity.title ?? practitionerTitle

  const {
    items: eventItems,
    typing,
    processedClientIds,
  } = useMemo(
    () =>
      projectEventsToMessages(
        events,
        resolvedName,
        practitionerAvatar,
        liveCursor,
        true,
      ),
    [events, resolvedName, practitionerAvatar, liveCursor],
  )

  interface LocalMsg {
    id: string
    text: string
    ts: number
  }
  const [localMessages, setLocalMessages] = useState<LocalMsg[]>([])

  // The sorted timeline of beats, keeping the numeric `sort` so we can reason
  // about time gaps for grouping. Local (optimistic) messages already present
  // in events are filtered out to avoid duplicates.
  const rendered: RenderItem[] = useMemo(() => {
    const localItems: RenderItem[] = localMessages
      .filter((lm) => !processedClientIds.has(lm.id))
      .map((lm) => ({
        sort: lm.ts,
        // Optimistic local sends are always live — they're happening right now.
        live: true,
        msg: {
          id: lm.id,
          sentAt: formatTime(lm.ts),
          text: lm.text,
          user: { me: true, name: "You", status: "online" },
        },
      }))
    return [...eventItems, ...localItems].sort((a, b) => a.sort - b.sort)
  }, [eventItems, localMessages, processedClientIds])

  const messages: Message[] = useMemo(
    () => rendered.map((r) => r.msg),
    [rendered],
  )

  // Group flags per beat: a run starts when the previous beat has a different
  // speaker or lands more than GROUP_BREAK_MS earlier, and ends symmetrically.
  // The first beat of a run carries the avatar/name/time; the last carries the
  // "you" timestamp — continuation beats render bare and tucked in tight.
  const groupFlags = useMemo(() => {
    const flags = new Map<string, { start: boolean; end: boolean }>()
    rendered.forEach((cur, i) => {
      const prev = rendered[i - 1]
      const next = rendered[i + 1]
      const start =
        !prev ||
        speakerKey(prev.msg) !== speakerKey(cur.msg) ||
        cur.sort - prev.sort > GROUP_BREAK_MS
      const end =
        !next ||
        speakerKey(next.msg) !== speakerKey(cur.msg) ||
        next.sort - cur.sort > GROUP_BREAK_MS
      flags.set(cur.msg.id, { start, end })
    })
    return flags
  }, [rendered])

  // Reveal plan per beat: whether it animates at all, and (for live beats) its
  // staggered entrance delay. Historical backlog renders statically; only live
  // beats earn the ceremony. The stagger is batched per turn via seenIdsRef —
  // all segments of a turn arrive in one event, so we space out only the freshly
  // -mounted ones (already-shown beats won't re-animate on re-render anyway).
  const seenIdsRef = useRef<Set<string>>(new Set())
  const revealPlan = useMemo(() => {
    // Pacing (seconds). Live NPC beats unfold one after another. Narration gets
    // a longer lead-in before, and a held pause after, so the beat lands before
    // the speech that follows — and the speech no longer snaps in right as the
    // "typing" wait ends.
    const LEAD = 0.15 // held breath after typing stops, before the first beat
    const BUBBLE_GAP = 0.55 // dwell after a speech bubble
    const NARRATOR_LEAD = 0.4 // extra wait before narration shows
    const NARRATOR_GAP = 1.0 // longer dwell after narration — let it land

    const map = new Map<string, { animate: boolean; delay: number }>()
    let clock = LEAD
    for (const { msg, live } of rendered) {
      if (!live) {
        // Historical backlog (resume/replay): appears at once, no ceremony.
        map.set(msg.id, { animate: false, delay: 0 })
        continue
      }
      const fresh = !seenIdsRef.current.has(msg.id)
      if (!fresh || msg.user.me) {
        // Already on screen, or the user's own send: animate, but no stagger.
        map.set(msg.id, { animate: true, delay: 0 })
        continue
      }
      const isNarrator = msg.variant === "narrator"
      if (isNarrator) clock += NARRATOR_LEAD
      map.set(msg.id, { animate: true, delay: clock })
      clock += isNarrator ? NARRATOR_GAP : BUBBLE_GAP
    }
    return map
  }, [rendered])

  useEffect(() => {
    for (const { msg } of rendered) seenIdsRef.current.add(msg.id)
  }, [rendered])

  // Follow the conversation to the bottom on new beats and while the NPC is
  // typing, but only when the user is already parked there (see handleScroll).
  useEffect(() => {
    const el = scrollRef.current
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight
  }, [messages, typing])

  const handleSendMessage = async (message: string) => {
    if (!conversationId || !message.trim()) return
    const clientMsgId = `m_${Date.now()}`
    const now = Date.now()

    // Clear composition state
    setCompositionStartTime(null)
    setDraftText("")
    setLastMessageTime(now)

    // Optimistic local append
    setLocalMessages((prev) => [
      ...prev,
      { id: clientMsgId, text: message, ts: now },
    ])

    try {
      // Include composition metadata in the message
      const compositionDuration = compositionStartTime
        ? now - compositionStartTime
        : 0

      await postJson(`/api/conversations/${conversationId}/messages`, {
        content: message,
        client_msg_id: clientMsgId,
        client_ts: now,
        input_method: "keyboard",
        edit_duration_ms: compositionDuration,
      })
    } catch (e) {
      // In a real app, mark failed; for spike, console error
      console.error("Failed to send message", e)
    }
  }

  const handleHelpRequested = () => {
    // Emit help requested event - use void to explicitly ignore the promise
    void (async () => {
      if (emitEvent) {
        await emitEvent("help_requested", {
          context: "message_composition",
          message_draft: draftText,
          time_since_last_message_ms: Date.now() - lastMessageTime,
          help_type: "examples",
        })
      }
      console.log("Help requested - AI assistance coming soon")
    })()
  }

  // Handle composition tracking
  const handleCompositionStart = () => {
    // Use void to explicitly ignore the promise
    void (async () => {
      if (!compositionStartTime && emitEvent) {
        const now = Date.now()
        setCompositionStartTime(now)

        await emitEvent("message_composition_started", {
          trigger: "focus",
          time_since_last_message_ms: now - lastMessageTime,
        })
      }
    })()
  }

  const handleCompositionChange = (text: string) => {
    setDraftText(text)

    // Clear any existing timeout
    if (compositionTimeoutRef.current) {
      clearTimeout(compositionTimeoutRef.current)
    }

    // Set a timeout to detect significant pauses
    compositionTimeoutRef.current = window.setTimeout(() => {
      void (async () => {
        if (text.length > 0 && emitEvent) {
          await emitEvent("message_composition_changed", {
            current_length: text.length,
            change_type: "typing",
            significant_pause_ms: 3000,
          })
        }
      })()
    }, 3000)
  }

  const handleCompositionBlur = () => {
    // Use void to explicitly ignore the promise
    void (async () => {
      if (compositionStartTime && draftText.length > 0 && emitEvent) {
        await emitEvent("message_composition_abandoned", {
          draft_text: draftText,
          composition_duration_ms: Date.now() - compositionStartTime,
          draft_length: draftText.length,
          reason: "navigation",
        })
      }
    })()
  }

  return (
    <div
      className={`bg-primary flex h-full flex-1 flex-col transition-all duration-500 ease-in-out dark:bg-neutral-900 ${isBlurred ? "pointer-events-none blur-sm" : "blur-0"} ${!isFocused ? "cursor-pointer" : ""} `}
      onClick={!isFocused ? onFocus : undefined}
    >
      {/* Header */}
      <div className="bg-secondary border-b border-neutral-200 px-6 py-4 dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar
              initials={initialsFor(resolvedName)}
              src={practitionerAvatar}
              alt={resolvedName}
              status="online"
              size="md"
            />
            <div>
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-white">
                {resolvedName}
              </h2>
              <p className="text-xs text-neutral-600 dark:text-neutral-400">
                {resolvedTitle}
              </p>
            </div>
          </div>

          <div className="ml-4 flex items-center gap-2">
            {conversationId ? (
              <span className="rounded bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-200 ring-inset">
                Connected
              </span>
            ) : (
              <span className="rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600 ring-1 ring-neutral-200 ring-inset">
                Idle
              </span>
            )}
            {karaokeMode && (
              <Mic
                className="h-4 w-4 text-neutral-400 dark:text-neutral-500"
                aria-label="Karaoke mode"
              />
            )}
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="bg-tertiary flex-1 overflow-y-auto px-6 py-6 dark:bg-neutral-950"
      >
        <ul>
          {messages.map((msg, i) => {
            const prev = messages[i - 1]
            const flags = groupFlags.get(msg.id)
            const groupStart = flags?.start ?? true
            const groupEnd = flags?.end ?? true
            const plan = revealPlan.get(msg.id)

            // Spacing: keep staccato narrator beats reading as one unfolding
            // moment; otherwise a fresh group gets the larger gap and a
            // same-speaker continuation tucks in tight beneath its run.
            const tightNarrator =
              i > 0 && prev.variant === "narrator" && msg.variant === "narrator"
            const marginClass =
              i === 0
                ? ""
                : tightNarrator
                  ? "mt-1.5"
                  : groupStart
                    ? "mt-6"
                    : "mt-1"
            return (
              <MessageItem
                key={msg.id}
                msg={msg}
                className={`w-full ${marginClass}`}
                animateEntrance={plan?.animate ?? true}
                revealDelay={plan?.delay ?? 0}
                showAvatar={groupStart}
                showHeader={groupStart}
                showTimestamp={groupEnd}
                // Hard-coded off for now — the NPC header timestamps read as
                // distracting. Grouping logic above still runs, so flip this
                // back to a dynamic value (e.g. groupStart) to restore them.
                showHeaderTimestamp={false}
              />
            )
          })}
        </ul>

        {typing && (
          <div className="mt-6 flex items-center gap-2 text-sm text-neutral-500">
            <div className="flex gap-1">
              <span className="size-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.3s]" />
              <span className="size-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.15s]" />
              <span className="size-1.5 animate-bounce rounded-full bg-neutral-400" />
            </div>
            <span>{resolvedName} is typing…</span>
          </div>
        )}

        {/* The finale: the "What we saw" debrief card, once the closing turn's
            session_debrief event has arrived. Mirrors the replay placement. */}
        {debrief && (
          <div className="mt-8">
            <DebriefSurface debrief={debrief} />
          </div>
        )}

        {/* Debug panel (RBAC-ready via localStorage 'npc_debug') */}
        {debugOn && events && events.length > 0 && (
          <div className="mt-6 rounded-md border border-dashed p-3 text-xs">
            <div className="mb-2 font-semibold">
              Debug: Events ({events.length})
            </div>
            <ul className="space-y-1">
              {events.slice(-10).map((e) => (
                <li
                  key={e.event_id}
                  className="overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  <span className="font-mono text-[11px]">[{e.cursor}]</span>
                  <span className="ml-2 font-medium">{e.type}</span>
                  {e.stage ? (
                    <span className="ml-2 text-neutral-500">
                      stage={e.stage}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Input Area (hidden in read-only mode) */}
      {!readOnly && (
        <div className="bg-secondary border-t border-neutral-200 px-6 py-4 dark:border-neutral-800 dark:bg-neutral-900">
          <SimulationMessageAction
            onSubmit={handleSendMessage}
            onHelpRequested={handleHelpRequested}
            onFocus={handleCompositionStart}
            onBlur={handleCompositionBlur}
            onValueChange={handleCompositionChange}
            injection={injection}
            textAreaClassName="h-20"
          />
        </div>
      )}
    </div>
  )
}
